import { useEffect, useState } from "react";
import { api } from "../api";
import { useAuth } from "../context/AuthContext";
import { useLang, fmtRM } from "../i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";

const RULE_BADGE = {
  EPF: "bg-blue-50 text-blue-700 border-blue-200",
  SOCSO: "bg-emerald-50 text-emerald-700 border-emerald-200",
  EIS: "bg-purple-50 text-purple-700 border-purple-200",
  PCB: "bg-amber-50 text-amber-700 border-amber-200",
};

export default function Settings() {
  const { user, tenant, setTenant } = useAuth();
  const { lang } = useLang();
  const bm = lang === "bm";
  const [rules, setRules] = useState([]);
  const [logs, setLogs] = useState([]);
  const [plans, setPlans] = useState({ plans: [], addons: [] });
  const [branding, setBranding] = useState({ primary_color: "#1E40AF", logo_url: "", subdomain: "" });
  const [companyName, setCompanyName] = useState("");
  const [busyPlan, setBusyPlan] = useState("");
  const canEdit = user?.role === "TENANT_ADMIN" || user?.role === "SUPER_ADMIN";

  const subscribe = async (planCode) => {
    setBusyPlan(planCode);
    try {
      const r = await api.post("/payments/checkout", { plan: planCode, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout error");
      setBusyPlan("");
    }
  };

  useEffect(() => {
    api.get("/statutory/rules").then((r) => setRules(r.data)).catch(() => {});
    if (["SUPER_ADMIN", "TENANT_ADMIN", "HR_ADMIN", "HR_MANAGER", "AUDITOR"].includes(user?.role)) {
      api.get("/audit-logs").then((r) => setLogs(r.data)).catch(() => {});
    }
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (tenant) {
      setBranding(tenant.branding || branding);
      setCompanyName(tenant.company_name || "");
    }
  }, [tenant]);

  const save = async () => {
    try {
      const r = await api.patch("/tenant", { company_name: companyName, branding });
      setTenant(r.data);
      toast.success(bm ? "Branding disimpan" : "Branding saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  return (
    <div data-testid="settings-page" className="space-y-6">
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{bm ? "Tetapan" : "Settings"}</h1>
      <Tabs defaultValue="whitelabel">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger data-testid="settings-tab-whitelabel" value="whitelabel">White Label</TabsTrigger>
          <TabsTrigger data-testid="settings-tab-subscription" value="subscription">{bm ? "Langganan" : "Subscription"}</TabsTrigger>
          <TabsTrigger data-testid="settings-tab-statutory" value="statutory">{bm ? "Pusat Statutory" : "Statutory Centre"}</TabsTrigger>
          {["SUPER_ADMIN", "TENANT_ADMIN", "HR_ADMIN", "HR_MANAGER", "AUDITOR"].includes(user?.role) && (
            <TabsTrigger data-testid="settings-tab-audit" value="audit">Audit Log</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="whitelabel" className="mt-5">
          <Card>
            <CardHeader><CardTitle className="text-base">{bm ? "Penjenamaan Tenant" : "Tenant Branding"}</CardTitle></CardHeader>
            <CardContent className="space-y-4 max-w-lg">
              <div className="space-y-1">
                <Label className="text-xs">{bm ? "Nama Syarikat" : "Company Name"}</Label>
                <Input data-testid="whitelabel-company-name" value={companyName} onChange={(e) => setCompanyName(e.target.value)} disabled={!canEdit} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Logo URL</Label>
                <Input data-testid="whitelabel-logo-url" value={branding.logo_url} onChange={(e) => setBranding({ ...branding, logo_url: e.target.value })} disabled={!canEdit} placeholder="https://..." />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">{bm ? "Warna Utama" : "Primary Colour"}</Label>
                  <div className="flex gap-2">
                    <input data-testid="whitelabel-color-picker" type="color" value={branding.primary_color}
                      onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })} disabled={!canEdit}
                      className="w-10 h-10 rounded border cursor-pointer" />
                    <Input value={branding.primary_color} onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })} disabled={!canEdit} className="font-mono" />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Subdomain</Label>
                  <div className="flex items-center gap-1">
                    <Input data-testid="whitelabel-subdomain" value={branding.subdomain} onChange={(e) => setBranding({ ...branding, subdomain: e.target.value })} disabled={!canEdit} />
                    <span className="text-xs text-slate-500 whitespace-nowrap">.hrplatform.com</span>
                  </div>
                </div>
              </div>
              <div className="border rounded-xl p-4 bg-slate-50 dark:bg-slate-900">
                <p className="text-xs text-slate-500 mb-2">{bm ? "Pratonton" : "Preview"}</p>
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold" style={{ backgroundColor: branding.primary_color }}>
                    {(companyName || "C")[0]}
                  </div>
                  <span className="font-display font-bold">{companyName || "Company"}</span>
                  <span className="ml-auto px-3 py-1.5 rounded-lg text-white text-xs font-semibold" style={{ backgroundColor: branding.primary_color }}>Button</span>
                </div>
              </div>
              {canEdit && <Button data-testid="whitelabel-save-button" className="bg-blue-800 hover:bg-blue-900" onClick={save}>{bm ? "Simpan" : "Save"}</Button>}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="subscription" className="mt-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {plans.plans.map((p) => (
              <Card key={p.code} data-testid={`sub-plan-${p.code.toLowerCase()}`} className={tenant?.plan === p.code ? "ring-2 ring-blue-800" : ""}>
                <CardHeader>
                  <CardTitle className="text-base flex items-center justify-between">
                    {p.name}
                    {tenant?.plan === p.code && <Badge className="bg-blue-800">{bm ? "Semasa" : "Current"}</Badge>}
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-sm">
                  {p.base_monthly ? (
                    <p className="font-mono font-bold">RM{p.base_monthly}<span className="text-xs font-normal text-slate-500"> base</span> + RM{p.price_pepm}/emp</p>
                  ) : (
                    <p className="font-bold">Custom</p>
                  )}
                  <p className="text-xs text-slate-500 mt-2">{tenant?.headcount ?? 0} {bm ? "pekerja aktif" : "active employees"}</p>
                  {p.base_monthly && tenant?.plan === p.code && (
                    <p className="text-sm font-semibold mt-2" data-testid="subscription-estimate">
                      {bm ? "Anggaran bulanan" : "Est. monthly"}: {fmtRM(p.base_monthly + p.price_pepm * (tenant?.headcount || 0))}
                    </p>
                  )}
                  {canEdit && tenant?.plan !== p.code && (
                    p.code === "ENTERPRISE" ? (
                      <Button data-testid="subscribe-enterprise" variant="outline" size="sm" className="mt-4 w-full"
                        onClick={() => (window.location.href = "mailto:sales@workaholic.com")}>
                        {bm ? "Hubungi Sales" : "Contact Sales"}
                      </Button>
                    ) : (
                      <Button data-testid={`subscribe-${p.code.toLowerCase()}`} size="sm" disabled={!!busyPlan}
                        className="mt-4 w-full bg-blue-800 hover:bg-blue-900" onClick={() => subscribe(p.code)}>
                        {busyPlan === p.code ? (bm ? "Memproses..." : "Processing...") : (bm ? "Langgan dengan Stripe" : "Subscribe with Stripe")}
                      </Button>
                    )
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
          <Card className="mt-4">
            <CardHeader><CardTitle className="text-base">Add-Ons</CardTitle></CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              {plans.addons.map((a) => <Badge key={a} variant="outline">{a}</Badge>)}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="statutory" className="mt-5">
          <Card>
            <CardHeader><CardTitle className="text-base">{bm ? "Pusat Pematuhan Malaysia" : "Malaysia Compliance Centre"}</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{bm ? "Badan" : "Body"}</TableHead>
                    <TableHead>{bm ? "Versi" : "Version"}</TableHead>
                    <TableHead>{bm ? "Berkuat Kuasa" : "Effective"}</TableHead>
                    <TableHead className="hidden lg:table-cell">{bm ? "Sumber" : "Source"}</TableHead>
                    <TableHead>{bm ? "Ujian" : "Testing"}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rules.map((r) => (
                    <TableRow key={r.type} data-testid={`statutory-rule-${r.type.toLowerCase()}`}>
                      <TableCell><Badge variant="outline" className={RULE_BADGE[r.type] || ""}>{r.type}</Badge></TableCell>
                      <TableCell className="font-mono text-sm">{r.version}</TableCell>
                      <TableCell className="font-mono text-sm">{r.effective_date}</TableCell>
                      <TableCell className="hidden lg:table-cell text-xs text-slate-500 max-w-xs truncate">{r.source}</TableCell>
                      <TableCell><Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">{r.testing_status}</Badge></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <p className="text-xs text-slate-500 mt-3">
                {bm
                  ? "Setiap peraturan disimpan sebagai ruleset berversi — payroll baharu menggunakan versi mengikut tarikh kuat kuasa, payroll sejarah mengekalkan versi asal."
                  : "Each rule is stored as a versioned ruleset — new payrolls use the version by effective date; historical payrolls keep their original version."}
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="audit" className="mt-5">
          <Card>
            <CardContent className="pt-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{bm ? "Masa" : "Time"}</TableHead>
                    <TableHead>{bm ? "Pengguna" : "User"}</TableHead>
                    <TableHead>{bm ? "Tindakan" : "Action"}</TableHead>
                    <TableHead>Module</TableHead>
                    <TableHead className="hidden md:table-cell">Target</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((l) => (
                    <TableRow key={l.log_id}>
                      <TableCell className="font-mono text-xs">{new Date(l.created_at).toLocaleString("en-MY")}</TableCell>
                      <TableCell className="text-sm">{l.user_name}</TableCell>
                      <TableCell><Badge variant="outline">{l.action}</Badge></TableCell>
                      <TableCell className="text-sm">{l.module}</TableCell>
                      <TableCell className="hidden md:table-cell font-mono text-xs">{l.target}</TableCell>
                    </TableRow>
                  ))}
                  {logs.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-sm text-slate-500 py-6">-</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
